import { ExternalLink, FileText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { InsuranceIndication } from '../../models/insuranceIndication'
import { CheckoutEvidenceDialog } from './CheckoutEvidenceDialog'
import { CheckoutEvidenceSummary } from './CheckoutEvidenceSummary'
import { IndicationScreenshotsDialog } from './IndicationScreenshotsDialog'

/**
 * Everything you can *do* with an indication, gathered in the details sidebar.
 *
 * These used to sit along the top edge of the card, where they collided with
 * the carrier name and the quote status on a narrow column. The card is for
 * reading the numbers; the sidebar is where you act on them.
 *
 * No divider of its own — the coverage list above already ends in one.
 */
export function IndicationDetailActions({
  indication,
  isStaff,
  isCaiAdmin,
  links,
  children,
}: {
  indication: InsuranceIndication
  isStaff?: boolean
  isCaiAdmin?: boolean
  /** Page-specific links, grouped with the shared ones below the buttons. */
  links?: React.ReactNode
  /** Page-specific CTAs, stacked above the links. */
  children?: React.ReactNode
}) {
  // Prefer the deep link that reopens this specific quote — the carrier's
  // resume URL, resolved from its retention id when the run finished. The
  // provider portal link only reaches the dashboard root, leaving you to
  // search for the quote by hand. Responses run through toCamelCase, which
  // camelizes nested metadata keys too — the backend writes
  // geico_dashboard_url.
  const quoteUrl = indication.metadata.geicoDashboardUrl as string | undefined
  const portalHref = quoteUrl ?? indication.portalLink?.url
  const provider = indication.portalLink?.providerLabel ?? indication.vendorName
  const showPortalLink = !!isStaff && !!portalHref
  const showVendorId = !!isCaiAdmin && !!indication.vendorId

  const checkoutEvidence = isCaiAdmin ? indication.checkoutEvidence : undefined

  if (
    !indication.sourceDocumentUrl &&
    !showPortalLink &&
    !isStaff &&
    !showVendorId &&
    !checkoutEvidence &&
    !links &&
    !children
  ) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {children}
      {/* Every link together, under the buttons: they open something rather
          than doing something, so they read as one group. */}
      <div className="flex flex-col items-start gap-1">
        {indication.sourceDocumentUrl && (
          <Button
            size="sm"
            variant="link"
            className="h-auto justify-start px-0"
            href={indication.sourceDocumentUrl}
            target="_blank"
          >
            View quote document
            <FileText className="size-3" />
          </Button>
        )}
        {showPortalLink && (
          <Button
            size="sm"
            variant="link"
            className="h-auto justify-start px-0"
            href={portalHref}
            target="_blank"
          >
            View {provider} {quoteUrl ? 'Quote' : 'Portal'}
            <ExternalLink className="size-3" />
          </Button>
        )}
        {links}
        {isStaff && <IndicationScreenshotsDialog indication={indication} />}
        {checkoutEvidence && <CheckoutEvidenceDialog indication={indication} />}
      </div>
      {checkoutEvidence && <CheckoutEvidenceSummary evidence={checkoutEvidence} />}
      {showVendorId && (
        <div className="text-muted-foreground text-sm font-medium">
          Vendor ID: <span className="font-mono select-all">{indication.vendorId}</span>
        </div>
      )}
    </div>
  )
}
